<template>
  <v-window-item class="fill-height">
    <div class="d-flex fill-height flex-column">
      <ActionBar
        class="pa-3"
        :disabled="isLocked"
        show-import
        :disabled-import="schematisationStagesToImport.length === 0"
        @import="toggleImportDialog(true)"
        show-save
        @save="onSaveClicked"
        :loading-save="writeLoading"
        show-add
        @add="addReinforcement"
      >
        <template v-slot:next>
          <ActionButton
            class="mr-2"
            :disabled="reinforcements.length === 0 || isLocked"
            :loading="deleteAllLoading"
            @action="toggleDeleteAllDialog"
          >
            Delete all
          </ActionButton>
        </template>
      </ActionBar>
      <TableActionMenu
        :selected-item="selectedItem"
        :show-menu="showMenu"
        @update:show-menu="showMenu = $event"
        :x="x"
        :y="y"
        :add-button="false"
        @action="onDeleteClicked(selectedItem.id)"
      />
      <NoDataAvailableCenter v-if="!reinforcements?.length" />
      <div class="overflow-y-auto fill-height">
        <v-expansion-panels v-model="openedIndex">
          <v-expansion-panel
            elevation="0"
            v-for="reinforcement in reinforcements"
            :key="reinforcement.id"
          >
            <v-divider />
            <v-expansion-panel-title
              v-slot="{ expanded }"
              @contextmenu.prevent="isLocked || showActionMenu({ $event, reinforcement })"
            >
              <ReinforcementExpandHeader
                :color="reinforcement.color"
                :open="expanded"
                :name="reinforcement.name"
                :soil-type="shownSoilType(reinforcement.base_soil_type)"
              />
            </v-expansion-panel-title>
            <v-expansion-panel-text class="fill-height">
              <v-row dense class="my-3">
                <v-col sm="12">
                  <v-text-field
                    color="primary"
                    variant="underlined"
                    @update:model-value="
                      itemChange({ item: reinforcement, parameter: 'name', value: $event })
                    "
                    :model-value="reinforcement.name"
                    :class="[
                      getDirtyClass(reinforcement, 'name'),
                      isLocked ? 'disable-events' : ''
                    ]"
                    label="Reinforcement Name"
                    hide-details
                  >
                  </v-text-field>
                </v-col>

                <v-col sm="12" md="9">
                  <v-select
                    variant="underlined"
                    color="primary"
                    :model-value="reinforcement.base_soil_type"
                    @update:model-value="
                      itemChange({
                        item: reinforcement,
                        parameter: 'base_soil_type',
                        value: $event
                      })
                    "
                    :class="[
                      getDirtyClass(reinforcement, 'base_soil_type'),
                      isLocked ? 'disable-events' : ''
                    ]"
                    :items="soilTypes"
                    item-title="name"
                    item-value="id"
                    label="Soil type"
                    hide-details
                  >
                  </v-select>
                </v-col>
                <v-col sm="12" md="3" density="compact">
                  <ColorSelector
                    @update:model-value="
                      itemChange({ value: $event, item: reinforcement, parameter: 'color' })
                    "
                    :model-value="reinforcement.color"
                    :class="[
                      getDirtyClass(reinforcement, 'color'),
                      isLocked ? 'disable-events' : ''
                    ]"
                  />
                </v-col>
              </v-row>

              <ReinforcementPointTable
                :items="reinforcementPoints(reinforcement.id)"
                :reinforcement-id="reinforcement.id"
                ref="reinforcementTableRefs"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <GenerateSchematisationDialog />
      <ImportFromPreviousStageDialog
        :schematisation-stages="schematisationStagesToImport"
        v-model="importDialog"
        :loading="importLoading"
        text="reinforcements"
        @close="toggleImportDialog(false)"
        @import="handleImport"
      />
      <DeleteAllReinforcementsDialog
        :value="deleteAllDialogValue"
        @delete="deleteAll(stabSchematisationStageId), toggleDeleteAllDialog()"
        @close="toggleDeleteAllDialog"
      />
    </div>
  </v-window-item>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import ReinforcementExpandHeader from '@/components/reinforcement/ReinforcementExpandHeader.vue'
import ColorSelector from '@/components/reinforcement/ColorSelector.vue'
import DirtyFieldsMixIn from '@/mixins/DirtyFieldsMixIn.vue'
import ReinforcementPointTable from '@/components/reinforcement/ReinforcementPointTable.vue'
import ShowActionMenuMixin from '@/mixins/ShowActionMenuMixin.vue'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import GenerateSchematisationDialog from '@/components/reinforcement/GenerateSchematisationDialog.vue'
import ImportFromPreviousStageDialog from '@/components/schematisationDetail/ImportFromPreviousStageDialog.vue'
import ActionBar from '@/components/common/ActionBar.vue'
import NoDataAvailableCenter from '@/components/soilcomposition/NoDataAvailableCenter.vue'
import ActionButton from '@/components/common/ActionButton.vue'
import DeleteAllReinforcementsDialog from '@/components/reinforcement/DeleteAllReinforcementsDialog.vue'

export default {
  name: 'OptionsTab',
  components: {
    DeleteAllReinforcementsDialog,
    ActionButton,
    NoDataAvailableCenter,
    ActionBar,
    ImportFromPreviousStageDialog,
    GenerateSchematisationDialog,
    TableActionMenu,
    ReinforcementPointTable,
    ColorSelector,
    ReinforcementExpandHeader
  },
  mixins: [DirtyFieldsMixIn, ShowActionMenuMixin],
  data: () => ({
    selectedItem: null,
    showMenu: false,
    openedIndex: undefined
  }),
  computed: {
    ...mapState('stabSchematisationStage', {
      stabSchematisationStageId: (state) => state.currentItem.id
    }),
    ...mapState('baseSoilType', {
      soilTypes: (state) => state.items
    }),
    ...mapState('reinforcement', {
      importLoading: (state) => state.importLoading,
      importDialog: (state) => state.importDialog,
      writeLoading: (state) => state.writeLoading,
      deleteAllLoading: (state) => state.deleteAllLoading,
      deleteAllDialog: (state) => state.deleteAllDialog
    }),
    ...mapGetters('reinforcement', {
      reinforcements: 'sortedReinforcement',
      shownSoilType: 'shownSoilType'
    }),
    ...mapGetters('reinforcementPoint', {
      reinforcementPointById: 'reinforcementPoints'
    }),
    ...mapGetters('stabSchematisation', {
      isLocked: 'isLockedCurrent'
    }),
    ...mapGetters('stabSchematisationStage', {
      schematisationStagesToImport: 'previousSchematisationStagesHaveReinforcement'
    }),
    deleteAllDialogValue: {
      get() {
        return this.deleteAllDialog
      },
      set() {
        this.toggleDeleteAllDialog()
      }
    }
  },
  watch: {
    openedIndex(newIndex) {
      if (typeof newIndex === 'undefined') {
        this.setCurrentReinforcementPoint(null)
      }
      const currentReinforcement = this.reinforcements[newIndex]
      this.setCurrentItem(currentReinforcement || null)
    }
  },
  methods: {
    ...mapActions('reinforcement', {
      createReinforcement: 'createItem',
      setParameter: 'setParameter',
      updateReinforcements: 'update',
      toggleWriteLoading: 'toggleWriteLoading',
      deleteItem: 'deleteItem',
      deleteAll: 'deleteAll',
      toggleDeleteAllDialog: 'toggleDeleteAllDialog',
      setCurrentItem: 'setCurrentItem',
      emptyDirtyItems: 'emptyDirtyItems',
      toggleImportDialog: 'toggleImportDialog',
      importReinforcementsFromStage: 'importReinforcementsFromStage'
    }),
    ...mapActions('reinforcementPoint', {
      updateReinforcementPoints: 'update',
      createReinforcementPoints: 'create',
      setCurrentReinforcementPoint: 'setCurrentItem',
      deleteReinforcementPointsById: 'deleteByReinforcementId',
      emptyPointDirtyItems: 'emptyDirtyItems'
    }),
    async addReinforcement() {
      await this.createReinforcement({ stab_schematisation_stage: this.stabSchematisationStageId })
    },
    itemChange({ item, parameter, value }) {
      this.addDirty(item, parameter)
      this.setParameter({ item, parameter, value })
    },
    async onSaveClicked() {
      try {
        this.toggleWriteLoading()
        await this.saveChanges()
      } catch (e) {
        console.log(e)
      } finally {
        this.toggleWriteLoading()
      }
    },
    onDeleteClicked(reinforcementId) {
      this.deleteReinforcementPointsById(reinforcementId)
      this.deleteItem(reinforcementId)
    },
    async saveChanges() {
      await this.updateReinforcements()
      await this.createReinforcementPoints()
      await this.updateReinforcementPoints()
      this.cleanUpDirtyItems()
    },
    reinforcementPoints(reinforcementId) {
      return this.reinforcementPointById(reinforcementId)
    },
    showActionMenu({ $event, reinforcement }) {
      if (typeof this.openedIndex === 'undefined') {
        this.show($event, reinforcement)
      }
    },
    cleanUpDirtyItems() {
      this.emptyDirtyItems()
      this.emptyPointDirtyItems()
      this.dirtyItems = []
      const reinforcementTableRefs = this.$refs.reinforcementTableRefs
      if (reinforcementTableRefs) {
        for (const tableRef of reinforcementTableRefs) {
          tableRef.emptyDirtyItemsClass()
        }
      }
    },
    async handleImport(sourceSchematisationStageId) {
      try {
        this.setCurrentReinforcementPoint(null)
        this.setCurrentItem(null)
        this.cleanUpDirtyItems()
        await this.importReinforcementsFromStage({
          sourceSchematisationStageId,
          targetSchematisationStageId: this.stabSchematisationStageId
        })
      } catch (e) {
        console.log(e)
      } finally {
        this.toggleImportDialog(false)
      }
    }
  },
  destroyed() {
    this.setCurrentItem(null)
  }
}
</script>

<style scoped>
.v-input {
  font-size: 14px;
}

.text-label {
  height: 100%;
  display: flex;
  align-items: flex-end;
  font-size: 14px;
}
</style>
